import React, { useMemo, useState } from 'react';
import {
  Box,
  CardContent,
  Typography,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Pagination,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Recommendations from './Recommendations';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { AccordionContainer, CardContainer } from './styles';
import { useIdmsTasksQuery } from '@/__generated__/graphql';
import { formatDate } from '@/utils';
const ITEMS_PER_PAGE = 10;

const IdmsTask = () => {
  const structureId = useGetStructureId();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { data, loading } = useIdmsTasksQuery({
    variables: {
      structureId,
      pageInfo: {
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
      },
    },
  });

  const { idmsTasks, totalTasks } = useMemo(() => {
    return {
      idmsTasks: data?.idmsTasks.tasks ?? [],
      totalTasks: data?.idmsTasks.count ?? 0,
    };
  }, [data?.idmsTasks.count, data?.idmsTasks.tasks]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Box mt={2}>
      {idmsTasks.length > 0 && (
        <Typography variant="h3" mb={2}>
          Tasks
        </Typography>
      )}
      {!loading &&
        idmsTasks.length > 0 &&
        idmsTasks.map((task, index) => (
          <CardContainer key={index}>
            <CardContent>
              <Grid container spacing={2} mb={1}>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Line Number
                  </Typography>
                  <Typography variant="body1">{task?.lineNumber}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Task No
                  </Typography>
                  <Typography variant="body1">{task?.idmsTaskNumber}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Task Due Date
                  </Typography>
                  <Typography variant="body1">
                    {task?.taskDueDate ? formatDate(new Date(Number(task.taskDueDate))) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    {task?.status === null ? 'N/A' : task?.status}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Status Updated At
                  </Typography>
                  <Typography variant="body1">
                    {task?.statusUpdatedAt
                      ? formatDate(new Date(Number(task.statusUpdatedAt)))
                      : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {task?.createdAt ? formatDate(new Date(Number(task.createdAt))) : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              {task?.recommendations?.length > 0 && (
                <AccordionContainer elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{task?.recommendations.length} Recommendation/s</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Recommendations recommendations={task?.recommendations} />
                  </AccordionDetails>
                </AccordionContainer>
              )}
            </CardContent>
          </CardContainer>
        ))}
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={Math.ceil(totalTasks / ITEMS_PER_PAGE)}
          page={currentPage}
          onChange={(_, page) => handleChangePage(page)}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default IdmsTask;
